import { useCallback, useEffect, useState } from 'react';
import {
  TAKE_CHANGED_EVENT,
  TakeChangedDetail,
  TakeFields,
  pickTakeFields,
} from '../utils/takeState';

/**
 * An item's take_* fields as live state: seeded from the item payload,
 * re-seeded when a fresh payload arrives, and kept in step with every other
 * mounted view of the same item through the take-changed window event (a
 * toggle in the modal's card must show on the list row behind it, and on
 * the Take strip's card, without a refetch).
 */
export function useTakeFields(
  contentId: number | null | undefined,
  item: TakeFields | null | undefined,
): [TakeFields, (next: TakeFields) => void] {
  const [fields, setFields] = useState<TakeFields>(() => pickTakeFields(item));

  const requestedAt = item?.take_requested_at ?? null;
  const recordedAt = item?.take_recorded_at ?? null;
  useEffect(() => {
    setFields(pickTakeFields(item));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId, requestedAt, recordedAt]);

  useEffect(() => {
    if (contentId == null) return;
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<TakeChangedDetail>).detail;
      if (detail?.contentId === contentId) setFields(pickTakeFields(detail.take));
    };
    window.addEventListener(TAKE_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(TAKE_CHANGED_EVENT, onChange);
  }, [contentId]);

  const set = useCallback((next: TakeFields) => setFields(pickTakeFields(next)), []);
  return [fields, set];
}
